import { createContext, useState, useEffect, ReactNode } from "react";
import { useIsAuthenticated } from "@azure/msal-react";
import {
  clearAuthState,
  fetcher,
  isAuthDeniedStatus,
  isFetcherError,
} from "../utils/fetcher";
import { useAlert } from "./AlertContext";
import type { UserType } from "../types/common";
import type { VolunteerData } from "../types/volunteer";
import type { OrganisationData } from "../types/organisation";

interface UserInfo {
  id: string;
  user_type: UserType;
}

interface CurrentUser {
  userData: VolunteerData | OrganisationData;
  userId: string;
  userType: UserType;
  userRecommendedEvent: unknown | null;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  currentUser: CurrentUser | null;
  needsRegistration: boolean;
  refreshUser: () => void;
  editUser: (editedData: unknown, type: UserType) => Promise<void>;
  fetchVolunteer: (id: string) => Promise<void>;
  fetchOrganisation: (id: string) => Promise<void>;
  loading: boolean;
  error: boolean | string;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { setAlert } = useAlert();
  const isAuthenticated = useIsAuthenticated();

  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [needsRegistration, setNeedsRegistration] = useState<boolean>(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean | string>(false);

  useEffect(() => {
    const fetchEventMatch = async () => {
      setLoading(true);
      try {
        const data = await fetcher<unknown>({
          url: "/events/event-session/init",
          method: "POST",
        });
        setCurrentUser((prevState) => ({
          ...prevState!,
          userRecommendedEvent: data,
        }));
      } catch (error) {
        setError(
          "Došlo k chybě při načtení doporučených akcí, zkuste to prosím později.",
        );
        console.error("Error fetching recommended events:", error);
      } finally {
        setLoading(false);
      }
    };

    if (
      currentUser?.userType === "volunteer" &&
      "step_of_registration" in currentUser.userData &&
      currentUser.userData.step_of_registration > 2
    ) {
      fetchEventMatch();
    }
  }, [currentUser?.userData, currentUser?.userId, currentUser?.userType]);

  useEffect(() => {
    setError(false);

    const fetchUser = async () => {
      setLoading(true);
      setNeedsRegistration(false);
      try {
        const res = await fetcher<UserInfo>({ url: "/user" });
        const { id, user_type } = res;
        const data = await fetcher<VolunteerData | OrganisationData>({
          url: `/${user_type}/${id}`,
        });
        setCurrentUser((prev) => ({
          userData: data,
          userId: id,
          userType: user_type,
          userRecommendedEvent:
            prev?.userId === id ? (prev.userRecommendedEvent ?? null) : null,
        }));
      } catch (error) {
        console.error("Error fetching user data:", error);
        if (isFetcherError(error) && error.status === 404) {
          setNeedsRegistration(true);
          setCurrentUser(null);
          setError(false);
          return;
        }
        if (isFetcherError(error) && isAuthDeniedStatus(error.status)) {
          await clearAuthState();
          setCurrentUser(null);
          setError(false);
          return;
        }
        setError(true);
        setAlert(
          true,
          "error",
          "Nepodařilo se nám načíst data o uživateli. Zkuste to prosím znovu.",
        );
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchUser();
    } else {
      setCurrentUser(null);
      setNeedsRegistration(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, refreshCounter]);

  const editUser = async (editedData: unknown, type: UserType) => {
    try {
      const data = await fetcher<VolunteerData | OrganisationData>({
        url: `/${type}/${currentUser?.userId}`,
        method: "PUT",
        body: JSON.stringify(editedData),
      });
      setCurrentUser((prevState) => ({ ...prevState!, userData: data }));
    } catch (error) {
      setError(
        "Došlo k chybě při editaci profilu usera, zkuste to prosím později.",
      );
      console.error("Error updating user:", error);
    }
  };

  const fetchVolunteer = async (id: string) => {
    try {
      const data = await fetcher<VolunteerData>({
        url: `/volunteer/${id}`,
      });
      setCurrentUser((prevState) => ({ ...prevState!, userData: data }));
    } catch (error) {
      setError(
        "Došlo k chybě při načtení profilu dobrovolníka, zkuste to prosím později.",
      );
      console.error("Error fetching volunteer:", error);
    }
  };

  const fetchOrganisation = async (id: string) => {
    try {
      const data = await fetcher<OrganisationData>({
        url: `/organisation/${id}`,
      });
      setCurrentUser((prevState) => ({ ...prevState!, userData: data }));
    } catch (error) {
      setError(
        "Došlo k chybě při načtení profilu organizace, zkuste to prosím později.",
      );
      console.error("Error fetching organisation:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        currentUser,
        needsRegistration,
        refreshUser: () => setRefreshCounter((c) => c + 1),
        editUser,
        fetchVolunteer,
        fetchOrganisation,
        loading,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
